import { Link } from "react-router-dom";

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-warm-white pt-24">
      <section className="py-12 md:py-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 md:px-12">
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-medium text-plum mb-4">
            Privacy Policy
          </h1>
          <p className="font-body text-plum/60 mb-10">Last updated: June 2026</p>

          <div className="space-y-8 font-body text-plum/80 leading-relaxed">
            <div>
              <h2 className="font-heading text-xl md:text-2xl text-plum mb-3">1. Introduction</h2>
              <p>
                People's Medicine ("we," "us," or "our"), operated by Trina Cobbler, is committed to protecting the privacy of our clients and website visitors. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our doula support services.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl text-plum mb-3">2. Information We Collect</h2>
              <p className="mb-3">We may collect the following types of information:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li><strong>Personal Information:</strong> Name, email address, phone number, and mailing address provided when you book a service, contact us, or create a client portal account.</li>
                <li><strong>Health Information:</strong> Pregnancy-related information, birth preferences, medical history, and other health details you voluntarily share during consultations to enable us to provide appropriate doula support.</li>
                <li><strong>Payment Information:</strong> Billing details processed through our secure third-party payment processors. We do not store credit card numbers on our systems.</li>
                <li><strong>Website Usage Data:</strong> IP address, browser type, pages visited, and other analytics data collected automatically when you browse our website.</li>
                <li><strong>Insurance Information:</strong> Insurance plan details provided for billing purposes.</li>
              </ul>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl text-plum mb-3">3. How We Use Your Information</h2>
              <ul className="list-disc ml-6 space-y-2">
                <li>To provide, personalize, and improve our doula support services</li>
                <li>To schedule appointments and manage your care</li>
                <li>To process payments and insurance claims</li>
                <li>To communicate with you about your sessions, services, and support</li>
                <li>To send relevant educational resources and wellness information (with your consent)</li>
                <li>To improve our website and service offerings</li>
                <li>To comply with legal obligations</li>
              </ul>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl text-plum mb-3">4. How We Protect Your Information</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal and health information. Client session notes and health information are stored securely and access is limited to authorized personnel only. Virtual sessions are conducted through secure, encrypted platforms.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl text-plum mb-3">5. Information Sharing</h2>
              <p className="mb-3">We do not sell, trade, or rent your personal information. We may share your information only in the following circumstances:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li><strong>With your consent:</strong> When you authorize us to share information with your healthcare providers or other parties.</li>
                <li><strong>Service providers:</strong> With trusted third-party service providers who assist us in operating our website, scheduling, and payment processing, bound by confidentiality agreements.</li>
                <li><strong>Insurance companies:</strong> To process claims on your behalf, with your authorization.</li>
                <li><strong>Legal requirements:</strong> When required by law, regulation, or legal process, or to protect the safety of our clients or others.</li>
              </ul>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl text-plum mb-3">6. Cookies and Tracking</h2>
              <p>
                Our website may use cookies and similar technologies to enhance your browsing experience and analyze website traffic. You can control cookie preferences through your browser settings. Third-party tools such as our chat widget and scheduling system may also use cookies to function properly.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl text-plum mb-3">7. Your Rights</h2>
              <p className="mb-3">You have the right to:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information (subject to legal retention requirements)</li>
                <li>Opt out of marketing communications at any time</li>
                <li>Request a copy of your data in a portable format</li>
              </ul>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl text-plum mb-3">8. Children's Privacy</h2>
              <p>
                Our website and services are not directed to individuals under the age of 18. We do not knowingly collect personal information from minors. If you believe we have inadvertently collected information from a minor, please contact us immediately.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl text-plum mb-3">9. Third-Party Links</h2>
              <p>
                Our website may contain links to third-party websites, including our scheduling platform, client portal, and chat widget. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl text-plum mb-3">10. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated effective date. We encourage you to review this policy periodically.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl text-plum mb-3">11. Contact Us</h2>
              <p>
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us at (856) 567-5907 or through our <a href="https://trina-cobbler.clientsecure.me/contact-widget" target="_blank" rel="noopener noreferrer" className="text-teal hover:underline">client portal</a>.
              </p>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-peach/50">
            <Link to="/" className="font-body text-teal hover:underline">&larr; Back to Home</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
